import { useContext, useEffect, useState } from 'react';
import {
  deleteContactInfo,
  postContactInfo,
  putContactInfo,
} from '../../../apis/profile.api';
import {
  handleCheckChangeForm,
  handleCheckFalsyForm,
} from '../../../common/checkForm';
import {
  handleDeleteForm,
  handleResetForm,
  handleSubmitForm,
} from '../../../common/formCallApi';
import {
  checkContactInfoConvention,
  renderSubmitFormContent,
} from '../../../common/formCommonFunc';
import {
  contactTypeData,
  contactTypeIcon,
  listIconContactForModal,
  listViewPermissionIcon,
  placeHolderForEachContactType,
  viewPermissionData,
} from '../../../constant/user-setting-modal-resources';
import { IModalProps as IProps } from '../../../interfaces/components/modal.interface';
import { IContact as IContactValue } from '../../../interfaces/profile.interface';
import FormLayout from '../../../layouts/FormLayout/FormLayout2';
import AuthContext from '../../../state/auth/AuthContext';
import { useModal } from '../../../state/modal-provider/ModalProvider';
import { initContactState } from '../../../state/user-setting-modal/initState';
import PrimaryInput from '../../input/PrimaryInput';
import SelectInput from '../../input/SelectInput2';

const requiredKeys = ['contactType'];
const UserContactModal = ({
  defaultData,
  cancelCallback,
  isModalCancelled,
}: IProps) => {
  const [contactValue, setContactValue] = useState<IContactValue>({
    contactType: contactTypeData[defaultData?.contactType]?.value ?? '',
    contactInfo: defaultData?.contactInfo ?? '',
    viewPermission: defaultData?.viewPermission ?? 'public',
  });

  const [isSubmit, setIsSubmit] = useState<boolean>(false);

  const { setLoading } = useContext(AuthContext);
  const { toast } = useModal();

  const handleSetState = (name: string, value: string) => {
    setContactValue((state) => ({ ...state, [name]: value }));
    setIsSubmit(false);
  };

  const handleToggleLeaveModal = () => {
    if (
      !handleCheckChangeForm(contactValue, {
        contactType: contactTypeData[defaultData?.contactType + 1]?.value ?? '',
        contactInfo: defaultData?.contactInfo ?? '',
        viewPermission: defaultData?.viewPermission ?? 'public',
      })
    ) {
      toast.warning({
        deleteTitle: 'Thoát',
        cancelTitle: 'Ở lại',
        onDelete: () => cancelCallback && cancelCallback(false),
        title: 'Thay đổi chưa lưu',
        content:
          'Thay đổi của bạn chưa được lưu. Bạn vẫn muốn thoát mà không lưu?',
      });
    } else {
      cancelCallback && cancelCallback(false);
    }
  };

  const resetForm = () =>
    handleResetForm({
      setValueState: setContactValue,
      initValueState: initContactState,
    });

  const deleteForm = () =>
    handleDeleteForm({
      runAsyncFunc: !!defaultData?._id,
      callback: () => setIsSubmit(false),
      asyncFunc: deleteContactInfo,
      handleResetForm: resetForm,
      id: defaultData?._id ?? '',
      formCancelCallBack: cancelCallback,
      toggleSuccessModalNoti: () =>
        toast.success({
          content: 'Xoá thông tin thành công.',
        }),
      toggleErrorModalNoti: () =>
        toast.error({
          content: 'Có lỗi xảy ra, vui lòng thử lại.',
        }),
      setLoading,
    });

  const validateContactInfo = () =>
    checkContactInfoConvention(
      contactValue.contactType,
      contactValue.contactInfo
    );

  const submitForm = () =>
    handleSubmitForm({
      callback: () => setIsSubmit(true),
      extraValidate: validateContactInfo,
      data: contactValue,
      requiredKeys,
      id: defaultData?._id ?? '',
      asyncFunc: !defaultData?._id ? postContactInfo : putContactInfo,
      formCancelCallBack: cancelCallback,
      toggleSuccessModalNoti: () =>
        toast.success({
          content: renderSubmitFormContent(defaultData?._id),
        }),
      toggleErrorModalNoti: () =>
        toast.error({
          content: 'Có lỗi xảy ra, vui lòng thử lại.',
        }),
      setLoading,
    });

  // warning when leave modal
  useEffect(() => {
    if (isModalCancelled) {
      handleToggleLeaveModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalCancelled]);

  return (
    <FormLayout
      deleteCallback={() =>
        toast.warning({
          title: 'Xoá mục thông tin liên hệ!',
          content: 'Bạn chắc chắn muốn xoá nội dung mục thông tin liên hệ?',
          deleteTitle: 'Xoá',
          cancelTitle: 'Huỷ',
          onDelete: deleteForm,
        })
      }
      saveCallback={submitForm}
      formName="Thông tin liên hệ"
      cancelCallback={handleToggleLeaveModal}
      disabledDeletButton={handleCheckFalsyForm(contactValue)}
    >
      <div className="sm:py-4 flex flex-col justify-center gap-7">
        <SelectInput
          data={contactTypeData}
          placeholder="Chọn loại thông tin liên hệ*"
          placeholderIcon={contactTypeIcon}
          propertyName="contactType"
          error={isSubmit && !contactValue.contactType}
          value={contactValue.contactType}
          setValue={handleSetState}
          isObject
          listIcon={listIconContactForModal}
        />
        {isSubmit && !contactValue.contactType && (
          <p className="text-red-400 -mt-5 -mb-3 text-sm">
            Vui lòng chọn loại thông tin liên hệ
          </p>
        )}
        <PrimaryInput
          desc={
            placeHolderForEachContactType.filter(
              (element) => element.contactType === contactValue.contactType
            )[0].placeholder
          }
          value={contactValue.contactInfo}
          setValue={handleSetState}
          isObject
          error={isSubmit && contactValue.contactType && !validateContactInfo()}
          disabled={contactValue.contactType === ''}
          inputName="contactInfo"
          icon={
            contactValue.contactType
              ? listIconContactForModal.filter(
                  (element) => element.name === contactValue.contactType
                )[0].icon
              : null
          }
        />
        {contactValue.contactType && isSubmit && (
          <>
            {!contactValue.contactInfo ? (
              <p className="text-red-400 -mt-5 -mb-3 text-sm">
                {`Vui lòng nhập ${
                  contactValue.contactType === 'personalPhone' ||
                  contactValue.contactType === 'workPhone'
                    ? 'số điện thoại'
                    : 'email'
                }`}
              </p>
            ) : (
              !validateContactInfo() && (
                <p className="text-red-400 -mt-5 -mb-3 text-sm">
                  {`${
                    contactValue.contactType === 'personalPhone' ||
                    contactValue.contactType === 'workPhone'
                      ? 'Số điện thoại không đúng'
                      : 'Email không đúng'
                  }, vui lòng nhập lại!`}
                </p>
              )
            )}
          </>
        )}
        <SelectInput
          data={viewPermissionData}
          propertyName="viewPermission"
          error={false}
          value={contactValue.viewPermission}
          setValue={handleSetState}
          isObject
          listIcon={listViewPermissionIcon}
        />
      </div>
    </FormLayout>
  );
};

export default UserContactModal;
